import { backgroundTransitionOnClick, Button, IconFont, Popover, Text } from '@byecode/ui'
import type { ActorEvent, RecordLikeProtocol } from '@lighthouse/core'
import { type ButtonItem, EVENT_TYPE, type RichTextContentProtocol } from '@lighthouse/core'
import { ActionButton, getWithItemId, getWithRecordId, useActionRunningLoadings, VIEW_CELL_DATA_KEY } from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

const SCxRecordOperatorWrapper = styled.div`
    display: flex;
    align-items: center;
`

const SCxTableOperator = styled(Button)<{ open?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    background-color: ${({ open }) => open && 'var(--color-gray-200)'};
`

const SCxList = styled.div`
    /* padding: 8px 0; */
    max-height: 200px;
    overflow: hidden auto;
`

const SCxItem = styled(Text)`
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    cursor: pointer;
    font-size: var(--font-size-normal);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-line-clamp: 1;

    ${backgroundTransitionOnClick}
`

const SCxItemIcon = styled(IconFont)`
    margin-right: 8px;
`

const SCxShownActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: 8px;
`

const ICON_SHOW_TYPES = new Set(['icon', 'icon-name'])
const NAME_SHOW_TYPES = new Set(['name', 'icon-name'])

interface ActionViewModel {
    item: ButtonItem
    eventId: string
    actorEvent?: ActorEvent
}

interface MoreActionViewModel extends ActionViewModel {
    title: string
    showIcon: boolean
    showName: boolean
}

interface ShownActionsProps {
    actionViewModels: ActionViewModel[]
    loadings: Record<string, boolean>
    onRenderButtonTitle: (v: RichTextContentProtocol) => string
    onActionClick: (eventId: string, actorEvent?: ActorEvent) => void
}

const ShownActions = React.memo(function ShownActions({
    actionViewModels,
    loadings,
    onRenderButtonTitle,
    onActionClick
}: ShownActionsProps) {
    return (
        <SCxShownActionsWrapper>
            {actionViewModels.map(({ item, eventId, actorEvent }) => {
                return (
                    <ActionButton
                        key={item.id}
                        radius="8"
                        disabled={loadings[eventId]}
                        config={item}
                        onClick={ev => {
                            ev.stopPropagation()
                            onActionClick(eventId, actorEvent)
                        }}
                        onRenderTitle={onRenderButtonTitle}
                    />
                )
            })}
        </SCxShownActionsWrapper>
    )
})

interface RecordOperatorProps {
    uniqueId: string
    recordId: string
    record?: RecordLikeProtocol
    actions?: ButtonItem[]
    isMobile?: boolean
    preShowCount?: number
    onActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol) => string
}

export const RecordOperator: React.FC<RecordOperatorProps> = ({
    uniqueId,
    recordId,
    record,
    actions,
    isMobile,
    preShowCount,
    onActionTrigger,
    onRenderButtonTitle
}) => {
    const [operatorOpened, setOperatorOpened] = useState(false)
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
    const isShowOperator = useMemo(() => actions?.length, [actions?.length])
    const recordUniqueId = useMemo(() => getWithRecordId(uniqueId, recordId), [uniqueId, recordId])

    const shownCount = isMobile ? 1 : preShowCount
    const [shownActions, moreActions] = useMemo(() => {
        if (actions) {
            if (!preShowCount) {
                return [[], actions]
            }
            return [actions.slice(0, shownCount), actions.slice(shownCount)]
        }
        return [[], []]
    }, [actions, preShowCount, shownCount])

    const handleOperatorOpen = useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation()
        // setOperatorOpened(true)
    }, [])

    const triggerAction = useCallback(
        (eventId: string, actorEvent?: ActorEvent) => {
            if (!actorEvent || !onActionTrigger) {
                return
            }
            handleActionTriggerWithLoading({
                id: eventId,
                actorEvent,
                trigger: (id, currentActorEvent) => {
                    return onActionTrigger(id, currentActorEvent, record)
                }
            })
        },
        [handleActionTriggerWithLoading, onActionTrigger, record]
    )

    const shownActionViewModels = useMemo<ActionViewModel[]>(() => {
        return shownActions.map(item => {
            return {
                item,
                eventId: getWithItemId(recordUniqueId, item.id),
                actorEvent: item.eventList?.find(ev => ev.type === EVENT_TYPE.CLICK)
            }
        })
    }, [recordUniqueId, shownActions])

    const moreActionViewModels = useMemo<MoreActionViewModel[]>(() => {
        return moreActions.map(item => {
            const showName = NAME_SHOW_TYPES.has(item.showType)
            return {
                item,
                eventId: getWithItemId(recordUniqueId, item.id),
                actorEvent: item.eventList?.find(ev => ev.type === EVENT_TYPE.CLICK),
                title: showName ? onRenderButtonTitle(item.name) : '',
                showIcon: ICON_SHOW_TYPES.has(item.showType),
                showName
            }
        })
    }, [moreActions, onRenderButtonTitle, recordUniqueId])

    useUpdateEffect(() => {
        const currentRecordKey = `[${VIEW_CELL_DATA_KEY}='${uniqueId}&${recordId}']`
        const recordBlockDom = document.querySelector(currentRecordKey)
        if (!recordBlockDom) {
            return
        }
        if (operatorOpened) {
            recordBlockDom.classList.add('record-active')
            return
        }
        recordBlockDom.classList.remove('record-active')
    }, [operatorOpened])

    if (!isShowOperator) {
        return null
    }

    return (
        <SCxRecordOperatorWrapper>
            <ShownActions
                actionViewModels={shownActionViewModels}
                loadings={loadings}
                onRenderButtonTitle={onRenderButtonTitle}
                onActionClick={triggerAction}
            />
            {moreActionViewModels.length > 0 && (
                <Popover width={180} opened={operatorOpened} onChange={setOperatorOpened} position="bottom-end" withinPortal>
                    <Popover.Target>
                        <SCxTableOperator
                            open={operatorOpened}
                            type="text"
                            size="sm"
                            icon={<IconFont type="DotsThree" />}
                            onClick={handleOperatorOpen}
                        />
                    </Popover.Target>

                    <Popover.Dropdown>
                        <SCxList onClick={ev => ev.stopPropagation()}>
                            {moreActionViewModels.map(({ item, eventId, actorEvent, title, showIcon, showName }) => {
                                return (
                                    <SCxItem
                                        key={item.id}
                                        onClick={() => {
                                            setOperatorOpened(false)
                                            triggerAction(eventId, actorEvent)
                                        }}
                                        lineClamp={1}
                                        whiteSpace="nowrap"
                                    >
                                        {showIcon && item.icon && <SCxItemIcon type={item.icon} />}
                                        {showName && title}
                                    </SCxItem>
                                )
                            })}
                        </SCxList>
                    </Popover.Dropdown>
                </Popover>
            )}
        </SCxRecordOperatorWrapper>
    )
}
